import * as React from 'react'
import styled from 'styled-components'
import { FormattedMessage, injectIntl } from 'react-intl'
import { Button, SelectButton } from 'Components/input'
import TabbedTwoColumn from 'Components/layout/tabbedTwoColumn'
import PdfDisplayError from 'Components/readonly/pdfDisplayError'
import axios from "axios";
import {GrowlContext} from "../../../Context";
//import { GrowlContext } from 'Context'



const Header = styled.div`
  display: flex;
  align-items: center;
  padding: 1rem;

  && {
    h2 {
      padding: 0 !important;
    }

    nav {
      display: flex;
      column-gap: 10px;
      margin-left: auto;
      button {
        margin: 0 !important;
      }
    }
  }
`

const Content = styled.div`
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
`

const ContentHeader = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 4px;

  @media screen and (max-width: 1280px) {
    flex: 0 0 50px;
  }

  @media screen and (max-width: 640px) {
    display: block;
    flex: 0 0 1%;
  }

  ${SelectButton}.approve-select-button {
    margin-bottom: 10px;

    .p-button {
      @media screen and (max-width: 640px) {
        .p-button-text {
          font-size: 12px;
        }
      }

      :first-child {
        &.p-highlight {
          &,
          &:hover {
            background-color: #ffffff;
            border-color: #ffffff;
            color: #404040;
          }
        }
      }

      :nth-child(2) {
        &:not(.p-highlight):hover {
          background-color: #d20f57;
          border-color: #d20f57;
          color: #ffffff;
        }

        &.p-highlight {
          &,
          &:hover {
            background-color: #d20f57;
            border-color: #d20f57;
            color: #ffffff;
          }
        }
      }

      :last-child {
        &:not(.p-highlight):hover {
          background-color: #3eb579;
          border-color: #3eb579;
          color: #ffffff;
        }

        &.p-highlight {
          &:hover {
            background-color: #3eb579;
            border-color: #3eb579;
            color: #ffffff;
          }
        }
      }
    }
  }
`

const Version = styled.div`
  text-align: right;

  @media screen and (max-width: 1280px) and (min-width: 641px) {
    display: none;
  }

  @media screen and (max-width: 640px) {
    padding: 8px 0;
    text-align: left;
  }
`

const Sds = styled.div`
  > ${Button} {
    display: none;
  }

  height: 100%;
  display: flex;
  flex: 1 1 auto;

  object {
    height: auto;
    flex: 1 1 auto;
  }

  @media screen and (max-width: 640px) {
    object {
      display: none;
    }

    > ${Button} {
      display: block;
    }
  }
`

const PdfRevision = injectIntl(({ intl, data, setData, reload }) => {
  const { displayError } = React.useContext(GrowlContext)
  const tabOptions = React.useMemo(() => {
    return [{
      text: "Svenska",
    }];
  }, []);

  /**
   * Initiates the download of a file by opening a new browser tab or window
   * pointing to the constructed download URL.
   *
   * Note: Ensure that `data.uri_compare` is defined and contains the base URL
   * before invoking this function to avoid errors.
   */
  const download = () => {
    window.open(`${data.uri_compare}/download`)
  }

  /**
   * Asynchronously submits an SDS (Safety Data Sheet) file and associated chemical data to the backend API.
   * The function constructs a `FormData` object containing various details such as registry ID, chemical name, supplier,
   * status, and the SDS file. This data is then sent via an `axios` POST request to the specified API endpoint.
   *
   * @param {Object} event - The event object triggered by the form submission or user interaction.
   *
   * @throws {Error} Logs the error and input data to the console if the API request fails.
   */
  const submitSDS = async (event) => {
    const formData = new FormData();

    const sdsFile = new File([data.sds_blob], 'sds-' + data.id + '.pdf', {
      type: 'application/pdf'
    });
    formData.append('sds_registry_id', data.id);
    formData.append('sds_id', data.current_sds);
    formData.append('sds', sdsFile);
    formData.append('language', data.language);
    try {
      await axios.post('/rapi/admin/sds-registry/' + data.chemical_id, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      reload();
    } catch (error) {
      displayError(error);
    }
  };

  return (
    <>
      <Header>
        <h2>{intl.formatMessage({ id: 'sds.compare-sds' })}</h2>
        <nav>
          <Button
            label={intl.formatMessage({ id: 'common.cancel' })}
            onClick={() => reload()}
          />
          <Button
            primary
            label={intl.formatMessage({ id: 'common.choose' })}
            onClick={submitSDS}
          />
        </nav>
      </Header>
      <TabbedTwoColumn
        tabOptions={tabOptions}
        leftContent={
          <Content>
            <ContentHeader>
              <Version>
                <FormattedMessage id="chemicalsds.new_version"/>
              </Version>
            </ContentHeader>
            {data && data?.uri && (
              <Sds>
                <object type="application/pdf" data={data.uri}>
                  <PdfDisplayError pdfUrl={data.uri}/>
                </object>
                <Button
                  label={intl.formatMessage({ id: 'sds.download_sdb' })}
                  icon="pi pi-download"
                  onClick={() => download()}
                />
              </Sds>
            )}
          </Content>
        }
        rightContent={
          <Content>
            <ContentHeader>
              <Version>
                <FormattedMessage id="chemicalsds.current_version"/>
              </Version>
            </ContentHeader>
            {data && data?.uri_compare && (
            <Sds>
              <object type="application/pdf" data={data.uri_compare}>
                <PdfDisplayError pdfUrl={data.uri_compare}/>
              </object>
              <Button
                label={intl.formatMessage({ id: 'sds.download_sdb' })}
                icon="pi pi-download"
                onClick={() => download()}
              />
            </Sds>
            )}
          </Content>
        }
      />
    </>
  )
})

export default PdfRevision;
